import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../../lib/axios";
import { toast } from "sonner";
import {
  FileText,
  Upload,
  Plus,
  Trash2,
  Save,
  X,
  Package,
  Truck,
  Ship,
  Calculator,
  Download,
  Image as ImageIcon,
  User,
  Edit2,
  Check,
  ChevronDown,
  ChevronUp,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  AlertCircle,
  CheckSquare,
  Square,
  FileSpreadsheet,
  Lock,
  GripVertical,
  Sparkles,
  Copy,
  RefreshCw,
  Eye,
  Printer,
  GitCompare
} from "lucide-react";
import {
  arrayMove,
} from '@dnd-kit/sortable';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import AddItemModal from "./AddItemModal";
import AutocompleteInput from "./AutocompleteInput";
import { CostSectionsGrid, CostSummaryCard } from "./CostSections";
import { ImagePreviewModal } from "./ImagePreviewModal";
import { PriceLookupModal } from "./PriceLookupModal";
import { PDFPreviewModal } from "./PDFPreviewModal";
import { ExcelExportModal } from "./ExcelExportModal";
// Extracted sub-components for better maintainability
import ClientInfoSection from "./ClientInfoSection";
import ServiceTypeSection from "./ServiceTypeSection";
import CommercialConsiderationsSection, { defaultConsiderations } from "./CommercialConsiderationsSection";
import HistorySection from "./HistorySection";
import ActionsBar from "./ActionsBar";
// Extracted table components
import SortableTableRow from "./SortableTableRow";
import BulkEditToolbar from "./BulkEditToolbar";
import PDFSectionsToggle from "./PDFSectionsToggle";
import ItemsTableCard from "./ItemsTableCard";
import QuotationItemsTable from "./QuotationItemsTable";
// New Otros section
import OtrosSection from "./OtrosSection";
// Revision Comparator
import RevisionComparator from "./RevisionComparator";
// PDF Export hook
import usePDFExport from "../../hooks/usePDFExport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value, currency = "USD") => {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }).format(value || 0);
};

const formatNumber = (value, decimals = 2) => {
  return (value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

// SortableTableRow moved to ./SortableTableRow.jsx

const QuotationEditorModal = ({ isOpen, onClose, quotation, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  const [isAddItemOpen, setIsAddItemOpen] = useState(false);
  const [clientInfoOpen, setClientInfoOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("draft");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [validUntil, setValidUntil] = useState("");
  
  // Local state for editing
  const [items, setItems] = useState([]);
  const [clientInfo, setClientInfo] = useState({});
  
  // Bulk edit state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [modifiedItems, setModifiedItems] = useState(new Set()); // Track modified items for visual feedback
  const [expandedItems, setExpandedItems] = useState(new Set()); // Track expanded items for detail view
  const [bulkDescription, setBulkDescription] = useState("");
  const [bulkSeries, setBulkSeries] = useState("");
  const [bulkMaterial, setBulkMaterial] = useState("");
  const [bulkColor, setBulkColor] = useState("");
  const [bulkPricePercent, setBulkPricePercent] = useState("");
  const [bulkFixedPrice, setBulkFixedPrice] = useState(""); // Fixed price per m² for bulk edit
  
  // Image preview modal
  const [previewImage, setPreviewImage] = useState(null);
  const [previewItemCode, setPreviewItemCode] = useState("");
  const [previewItemId, setPreviewItemId] = useState(null);
  const imageUploadRef = useRef(null);
  
  // PDF Preview modal
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewBlob, setPdfPreviewBlob] = useState(null);
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Excel export modal
  const [excelModalOpen, setExcelModalOpen] = useState(false);
  const [excelPassword, setExcelPassword] = useState("");
  const [exportingExcel, setExportingExcel] = useState(false);
  
  // Revision Comparator modal
  const [isComparatorOpen, setIsComparatorOpen] = useState(false);
  const [allQuotationsForCompare, setAllQuotationsForCompare] = useState([]);
  
  // Add Manual Type modal (to add new types to Lista de Precio Venta)
  const [isAddTypeOpen, setIsAddTypeOpen] = useState(false);
  const [newTypeData, setNewTypeData] = useState({
    name: "",
    material: "uPVC",
    series: "",
    color: "",
    width_m: "",
    height_m: "",
    cost: "",
    image_data: null
  });
  const [savingType, setSavingType] = useState(false);
  
  // Autocomplete suggestions from database
  const [autocompleteData, setAutocompleteData] = useState({
    descriptions: [],
    series: [],
    materials: [],
    colors: []
  });
  
  const [installation, setInstallation] = useState({
    uti_percent: 11,
    cos_m2i: 123,
    // Base values for calculation (rows 1-3 from Excel)
    emo_total: 120,      // EMO total
    epps_x_per: 100,     // EPPS X PER
    unid_desta: 20,      // UNID DESTA (BOT SIL)
    can_pers: 6,         // CAN. PERS (cantidad de personal)
    sello_total: 6580,   // SELLO total
    adicional: 0,        // ACC SOFT
    total: 0,
    uti_sum: 38          // % UTI SUM - default 38%
  });
  const [pdr, setPdr] = useState({
    quantity: 0,
    unit_value: 0,
    total: 0
  });
  const [freight, setFreight] = useState({
    container_quantity: 1,
    container_cost: 0,
    additional_costs: 0,
    total: 0
  });
  
  // Otros (miscellaneous items) state
  const [otrosItems, setOtrosItems] = useState([]);
  const [otrosCurrency, setOtrosCurrency] = useState("PEN"); // PEN or USD
  const [exchangeRate, setExchangeRate] = useState(3.75); // Default exchange rate
  const [showOtros, setShowOtros] = useState(true);
  
  // Section visibility toggles for PDF export
  const [showSupply, setShowSupply] = useState(true);
  const [showFreight, setShowFreight] = useState(true);
  const [showInstallation, setShowInstallation] = useState(true);
  const [showPdr, setShowPdr] = useState(true);

  // Service Type for PDF header (e.g., "SUMINISTRO E INSTALACIÓN DE VENTANAS Y PUERTAS DE UPVC")
  const [serviceType, setServiceType] = useState("SUMINISTRO E INSTALACIÓN DE VENTANAS Y PUERTAS DE UPVC");
  const [serviceTypeOpen, setServiceTypeOpen] = useState(false);
  
  // Commercial Considerations for PDF
  const [commercialConsiderations, setCommercialConsiderations] = useState([]);
  const [considerationsOpen, setConsiderationsOpen] = useState(false);
  const [newConsideration, setNewConsideration] = useState("");

  // Status configuration
  const statusConfig = {
    draft: { label: "Borrador", icon: Clock, color: "bg-slate-500" },
    sent: { label: "Enviada", icon: Send, color: "bg-blue-500" },
    approved: { label: "Aprobada", icon: CheckCircle2, color: "bg-green-500" },
    rejected: { label: "Rechazada", icon: XCircle, color: "bg-red-500" }
  };

  // Handle drag end for reordering items (sensors handled by QuotationItemsTable)
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(item => (item.id || items.indexOf(item)) === active.id);
      const newIndex = items.findIndex(item => (item.id || items.indexOf(item)) === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(items, oldIndex, newIndex);
        setItems(newItems);
        
        // Auto-save the new order to backend
        try {
          const itemIds = newItems.map(item => item.id).filter(id => id);
          if (itemIds.length === newItems.length && quotation?.id) {
            await api.patch(`${API}/quotations/${quotation.id}/reorder-items`, {
              item_ids: itemIds
            });
            toast.success(`Item movido a posición ${newIndex + 1}`);
          } else {
            toast.success(`Item movido a posición ${newIndex + 1} (guardar para persistir)`);
          }
        } catch (error) {
          // console.error("Error saving order:", error);
          toast.success(`Item movido a posición ${newIndex + 1}`);
        }
      }
    }
  };

  // Initialize from quotation
  useEffect(() => {
    if (quotation) {
      setItems(quotation.items || []);
      setClientInfo({
        client_company: quotation.client_company || "",
        client_contact: quotation.client_contact || "",
        client_address: quotation.client_address || "",
        client_phone: quotation.client_phone || "",
        client_email: quotation.client_email || ""
      });
      setInstallation(quotation.installation || { uti_percent: 0, cos_m2i: 0, total: 0, uti_sum: 38 });
      setPdr(quotation.pdr || { quantity: 0, unit_value: 0, total: 0 });
      setFreight(quotation.freight || { container_quantity: 1, container_cost: 0, additional_costs: 0, total: 0 });
      setCurrentStatus(quotation.status || "draft");
      setValidUntil(quotation.valid_until || "");
      // Otros items
      setOtrosItems(quotation.otros_items || []);
      setOtrosCurrency(quotation.otros_currency || "PEN");
      setExchangeRate(quotation.exchange_rate || 3.75);
      setShowOtros(quotation.show_otros !== false);
      // Section visibility
      setShowSupply(quotation.show_supply !== false);
      setShowFreight(quotation.show_freight !== false);
      setShowInstallation(quotation.show_installation !== false);
      setShowPdr(quotation.show_pdr !== false);
      // Service type and commercial considerations
      setServiceType(quotation.service_type || "SUMINISTRO E INSTALACIÓN DE VENTANAS Y PUERTAS DE UPVC");
      setCommercialConsiderations(quotation.commercial_considerations || defaultConsiderations);
    }
  }, [quotation]);

  // Fetch autocomplete data from database
  const fetchAutocomplete = async () => {
    try {
      const response = await api.get(`${API}/quotations/autocomplete`);
      setAutocompleteData({
        descriptions: response.data.descriptions || [],
        series: response.data.series || [],
        materials: response.data.materials || [],
        colors: response.data.colors || []
      });
    } catch (error) {
      console.error("Error fetching autocomplete data:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAutocomplete();
    }
  }, [isOpen]);

  // Fetch history
  const fetchHistory = useCallback(async () => {
    if (!quotation?.id) return;
    setLoadingHistory(true);
    try {
      const response = await api.get(`${API}/quotations/${quotation.id}/history`);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoadingHistory(false);
    }
  }, [quotation?.id]);

  // Load history when panel opens
  useEffect(() => {
    if (historyOpen && quotation?.id) {
      fetchHistory();
    }
  }, [historyOpen, quotation?.id, fetchHistory]);

  // Handle Escape key for image preview
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && previewImage) {
        setPreviewImage(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [previewImage]);

  // Handle paste image from clipboard
  useEffect(() => {
    const handlePaste = async (e) => {
      // Only handle paste when image preview modal is open
      if (!previewImage || !previewItemId) return;
      
      const clipboardItems = e.clipboardData?.items;
      if (!clipboardItems) return;
      
      for (const item of clipboardItems) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (blob) {
            const reader = new FileReader();
            reader.onload = async (event) => {
              const newImageData = event.target.result;
              
              // Find the item index for auto-lookup
              const itemIndex = items.findIndex(itm => itm.id === previewItemId || itm === previewItemId);
              const currentItem = items[itemIndex];
              
              // Update the item's image
              setItems(prevItems => prevItems.map((itm, idx) => {
                if (itm.id === previewItemId || idx === previewItemId) {
                  return { ...itm, image_data: newImageData };
                }
                return itm;
              }));
              setPreviewImage(newImageData);
              toast.success(`Imagen pegada para ${previewItemCode}`);
              
              // Auto-lookup price based on description after pasting image
              if (currentItem && itemIndex >= 0) {
                try {
                  const response = await api.post(`${API}/quotations/auto-lookup-price`, {
                    description: currentItem.description || "",
                    series: currentItem.series || "",
                    material: currentItem.material || "",
                    color: currentItem.color || ""
                  });
                  
                  if (response.data.match) {
                    const match = response.data.match;
                    setItems(prevItems => prevItems.map((itm, idx) => {
                      if (itm.id === previewItemId || idx === previewItemId) {
                        const cost = match.cost_per_m2 || match.average_cost_per_m2 || 0;
                        return { 
                          ...itm, 
                          image_data: newImageData,
                          cost_per_m2: cost,
                          matched_type_id: match.id,
                          matched_type_name: match.name,
                          calculated_value: (itm.area_m2 || 0) * cost * (itm.quantity || 1)
                        };
                      }
                      return itm;
                    }));
                    toast.success(`Precio encontrado: $${(match.cost_per_m2 || match.average_cost_per_m2)?.toFixed(2)}/m² de "${match.name}"`);
                  }
                } catch (error) {
                  console.error("Auto-lookup after paste failed:", error);
                }
              }
            };
            reader.readAsDataURL(blob);
          }
          break;
        }
      }
    };
    
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [previewImage, previewItemId, previewItemCode, items]);

  // Calculate totals
  // Apply % UTI SUM to each item's total
  const utiSumPercent = typeof installation.uti_sum === 'number' ? installation.uti_sum : 38;
  const utiSumMultiplier = 1 + utiSumPercent / 100;
  
  // Calculate item totals with UTI SUM applied
  const subtotalSupply = items.reduce((sum, item) => {
    const baseValue = item.calculated_value || 0;
    const valueWithUtiSum = baseValue * utiSumMultiplier;
    return sum + valueWithUtiSum;
  }, 0);
  
  const totalM2 = items.reduce((sum, item) => sum + ((item.area_m2 || 0) * (item.quantity || 1)), 0);
  
  // Installation total calculation using full formula:
  // Calculate values per m2 based on total m2 of project
  const projectM2 = totalM2 || 1;  // Avoid division by zero
  const emoTotal = parseFloat(installation.emo_total) || 120;
  const eppsXPer = parseFloat(installation.epps_x_per) || 100;
  const unidDesta = parseFloat(installation.unid_desta) || 20;
  const canPers = parseFloat(installation.can_pers) || 6;
  const selloTotal = parseFloat(installation.sello_total) || 6580;
  
  // Calculated values per m2
  const emoXM2 = (canPers * emoTotal) / projectM2;
  const destaXM2 = (canPers * unidDesta) / projectM2;
  const eppXM2 = (canPers * eppsXPer) / projectM2;
  const selloXM2 = selloTotal / projectM2;
  
  // COS FIN M2 = cos_m2i + emo + sello + desta + epp
  const baseCostM2 = (installation.cos_m2i || 123) + emoXM2 + selloXM2 + destaXM2 + eppXM2;
  
  const installationSubtotal = items.reduce((sum, item) => {
    const area = item.area_m2 || 0;
    const qty = item.quantity || 1;
    const description = (item.description || '').toUpperCase();
    // VENTANA ALTA uses 300% UTI
    const itemUtiPercent = description.includes('VENTANA ALTA') ? 300 : (installation.uti_percent || 11);
    const unitPricePerM2 = baseCostM2 * (1 + itemUtiPercent / 100);
    return sum + (area * unitPricePerM2 * qty);
  }, 0);
  
  const installationTotal = installationSubtotal + (installation.adicional || 0);
  const pdrTotal = (pdr.quantity || 0) * (pdr.unit_value || 0);
  const freightTotal = (freight.container_quantity || 0) * (freight.container_cost || 0) + (freight.additional_costs || 0);
  
  // Otros calculations with IGV
  const IGV_RATE = 0.18;
  const otrosSubtotal = otrosItems.reduce((sum, item) => sum + (item.total || 0), 0);
  const otrosIgv = otrosSubtotal * IGV_RATE;
  const otrosTotal = otrosSubtotal + otrosIgv;
  
  // Convert otros to USD if needed for grandTotal calculation
  // Note: This is a simplification - in production you'd want exchange rate handling
  const otrosTotalUSD = otrosCurrency === "USD" ? otrosTotal : 0; // Only include if in USD
  const otrosTotalPEN = otrosCurrency === "PEN" ? otrosTotal : 0; // Only include if in PEN
  
  // Grand total includes USD amounts only (otros in soles shown separately)
  const grandTotal = subtotalSupply + installationTotal + pdrTotal + freightTotal + otrosTotalUSD;
  
  // Helper function to get item total with UTI SUM applied
  const getItemTotalWithUtiSum = (item) => {
    const baseValue = item.calculated_value || 0;
    return baseValue * utiSumMultiplier;
  };

  // Handle adding new type to Lista de Precio Venta
  const handleSaveNewType = async () => {
    if (!newTypeData.name || !newTypeData.width_m || !newTypeData.height_m || !newTypeData.cost) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }
    
    setSavingType(true);
    try {
      const payload = {
        name: newTypeData.name.toUpperCase(),
        material: newTypeData.material,
        series: newTypeData.series || null,
        color: newTypeData.color || null,
        width_m: parseFloat(newTypeData.width_m),
        height_m: parseFloat(newTypeData.height_m),
        cost: parseFloat(newTypeData.cost),
        image_data: newTypeData.image_data
      };
      
      const response = await api.post(`${API}/quote-types/manual`, payload);
      toast.success(response.data.message || "Tipo agregado exitosamente");
      
      // Reset form and close modal
      setNewTypeData({
        name: "",
        material: "uPVC",
        series: "",
        color: "",
        width_m: "",
        height_m: "",
        cost: "",
        image_data: null
      });
      setIsAddTypeOpen(false);
      
      // Refresh autocomplete data
      fetchAutocomplete();
    } catch (error) {
      // console.error("Error saving type:", error);
      toast.error(error.response?.data?.detail || "Error al guardar el tipo");
    } finally {
      setSavingType(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post(
        `${API}/quotations/${quotation.id}/import-windoorcraft`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`Importados ${response.data.items_count} elementos`);
      // Refresh quotation data
      const updated = await api.get(`${API}/quotations/${quotation.id}`);
      setItems(updated.data.items || []);
      onUpdated();
    } catch (error) {
      // console.error("Error uploading file:", error);
      toast.error("Error al importar archivo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Handle item change
  const handleItemChange = async (index, field, value, shouldAutoLookup = false) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculate if dimensions or cost changed
    if (['width_m', 'height_m', 'quantity', 'cost_per_m2'].includes(field)) {
      const item = newItems[index];
      const area = (parseFloat(item.width_m) || 0) * (parseFloat(item.height_m) || 0);
      newItems[index].area_m2 = area;
      newItems[index].calculated_value = area * (parseFloat(item.cost_per_m2) || 0) * (parseInt(item.quantity) || 1);
    }
    
    setItems(newItems);
    
    // Mark item as modified (visual feedback)
    setModifiedItems(prev => new Set(prev).add(index));
    
    // Auto-lookup price only when explicitly requested (from autocomplete selection)
    if (shouldAutoLookup && ['description', 'series', 'material', 'color'].includes(field)) {
      const item = newItems[index];
      try {
        const response = await api.post(`${API}/quotations/auto-lookup-price`, {
          description: item.description || "",
          series: item.series || "",
          material: item.material || "",
          color: item.color || "",
          image_hash: item.image_hash || null
        });
        
        if (response.data.match) {
          const match = response.data.match;
          // Backend returns cost_per_m2 (mapped from average_cost_per_m2)
          const newCost = match.cost_per_m2 || match.average_cost_per_m2 || 0;
          
          if (newCost > 0) {
            // Update the item with new price
            const updatedItems = [...newItems];
            updatedItems[index].cost_per_m2 = newCost;
            updatedItems[index].matched_type_id = match.id;
            updatedItems[index].matched_type_name = match.name;
            
            // Recalculate value
            const area = updatedItems[index].area_m2 || 0;
            const quantity = updatedItems[index].quantity || 1;
            updatedItems[index].calculated_value = area * newCost * quantity;
            
            setItems(updatedItems);
            toast.success(`Precio encontrado: $${newCost.toFixed(2)}/m² de "${match.name}"`, { duration: 3000 });
          }
        }
      } catch (error) {
        console.error("Error auto-looking up price:", error);
      }
    }
  };

  // Handle item change with auto-lookup (for autocomplete selections)
  const handleItemChangeWithLookup = (index, field, value) => {
    handleItemChange(index, field, value, true);
  };

  // Handle delete item
  const handleDeleteItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    // Remove from selection if selected
    const newSelected = new Set(selectedItems);
    newSelected.delete(index);
    // Adjust indices for items after deleted one
    const adjustedSelected = new Set();
    newSelected.forEach(i => {
      if (i > index) {
        adjustedSelected.add(i - 1);
      } else {
        adjustedSelected.add(i);
      }
    });
    setSelectedItems(adjustedSelected);
  };

  // Toggle item selection
  const toggleItemSelection = (index) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedItems(newSelected);
  };

  // Toggle item expansion for detail view
  const toggleItemExpansion = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  // Toggle all items selection
  const toggleAllSelection = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map((_, i) => i)));
    }
  };

  // Apply bulk changes to selected items
  const applyBulkChanges = (field, value) => {
    if (selectedItems.size === 0) {
      toast.warning("Selecciona al menos un item");
      return;
    }
    if (!value.trim()) {
      toast.warning(`Ingresa un valor para ${field}`);
      return;
    }
    
    const newItems = items.map((item, index) => {
      if (selectedItems.has(index)) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setItems(newItems);
    toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} aplicado a ${selectedItems.size} items`);
  };

  // Apply all bulk changes at once
  const applyAllBulkChanges = async () => {
    if (selectedItems.size === 0) {
      toast.warning("Selecciona al menos un item");
      return;
    }
    
    let changes = 0;
    const newItems = [...items];
    const selectedIndices = Array.from(selectedItems);
    
    // First, apply the text changes
    for (const index of selectedIndices) {
      const item = { ...newItems[index] };
      if (bulkDescription.trim()) { item.description = bulkDescription; changes++; }
      if (bulkSeries.trim()) { item.series = bulkSeries; changes++; }
      if (bulkMaterial.trim()) { item.material = bulkMaterial; changes++; }
      if (bulkColor.trim()) { item.color = bulkColor; changes++; }
      newItems[index] = item;
    }
    
    if (changes === 0) {
      toast.warning("Ingresa al menos un valor para aplicar");
      return;
    }
    
    // Now, auto-lookup prices for each modified item
    let pricesUpdated = 0;
    for (const index of selectedIndices) {
      const item = newItems[index];
      try {
        const response = await api.post(`${API}/quotations/auto-lookup-price`, {
          description: item.description || "",
          series: item.series || "",
          material: item.material || "",
          color: item.color || "",
          image_hash: item.image_hash || null
        });
        
        if (response.data.match) {
          const match = response.data.match;
          // Backend returns cost_per_m2 (mapped from average_cost_per_m2)
          const newCost = match.cost_per_m2 || match.average_cost_per_m2 || 0;
          if (newCost > 0) {
            newItems[index].cost_per_m2 = newCost;
            newItems[index].matched_type_id = match.id;
            newItems[index].matched_type_name = match.name;
            
            // Recalculate value
            const area = newItems[index].area_m2 || 0;
            const quantity = newItems[index].quantity || 1;
            newItems[index].calculated_value = area * newCost * quantity;
            pricesUpdated++;
          }
        }
      } catch (error) {
        console.error("Error auto-looking up price for item:", error);
      }
    }
    
    setItems(newItems);
    
    // Mark all selected items as modified (visual feedback)
    setModifiedItems(prev => {
      const updated = new Set(prev);
      selectedIndices.forEach(idx => updated.add(idx));
      return updated;
    });
    
    if (pricesUpdated > 0) {
      toast.success(`Cambios aplicados a ${selectedItems.size} items. ${pricesUpdated} precios actualizados.`);
    } else {
      toast.success(`Cambios aplicados a ${selectedItems.size} items`);
    }
    
    // Clear bulk inputs
    setBulkDescription("");
    setBulkSeries("");
    setBulkMaterial("");
    setBulkColor("");
  };

  // Apply price percentage adjustment to selected items
  const applyPricePercentage = () => {
    if (selectedItems.size === 0) {
      toast.warning("Selecciona al menos un item");
      return;
    }
    
    const percent = parseFloat(bulkPricePercent);
    if (isNaN(percent)) {
      toast.warning("Ingresa un porcentaje válido");
      return;
    }
    
    const factor = 1 + (percent / 100);
    const newItems = [...items];
    
    for (const index of selectedItems) {
      const item = newItems[index];
      const currentPrice = item.cost_per_m2 || 0;
      const newPrice = Math.round(currentPrice * factor * 100) / 100; // Round to 2 decimals
      
      newItems[index].cost_per_m2 = newPrice;
      
      // Recalculate value
      const area = item.area_m2 || 0;
      const quantity = item.quantity || 1;
      newItems[index].calculated_value = area * newPrice * quantity;
    }
    
    setItems(newItems);
    
    // Mark modified items (visual feedback)
    setModifiedItems(prev => {
      const updated = new Set(prev);
      selectedItems.forEach(idx => updated.add(idx));
      return updated;
    });
    
    const direction = percent >= 0 ? "aumentado" : "reducido";
    toast.success(`Precio ${direction} ${Math.abs(percent)}% en ${selectedItems.size} items`);
    setBulkPricePercent("");
  };

  // Apply fixed price per m² to selected items
  const applyFixedPrice = () => {
    if (selectedItems.size === 0) {
      toast.warning("Selecciona al menos un item");
      return;
    }
    
    const fixedPrice = parseFloat(bulkFixedPrice);
    if (isNaN(fixedPrice) || fixedPrice < 0) {
      toast.warning("Ingresa un precio válido");
      return;
    }
    
    const newItems = [...items];
    
    for (const index of selectedItems) {
      const item = newItems[index];
      newItems[index].cost_per_m2 = fixedPrice;
      
      // Recalculate value
      const area = item.area_m2 || 0;
      const quantity = item.quantity || 1;
      newItems[index].calculated_value = area * fixedPrice * quantity;
    }
    
    setItems(newItems);
    
    // Mark modified items (visual feedback)
    setModifiedItems(prev => {
      const updated = new Set(prev);
      selectedItems.forEach(idx => updated.add(idx));
      return updated;
    });
    
    toast.success(`Precio fijo $${fixedPrice.toFixed(2)}/m² aplicado a ${selectedItems.size} items`);
    setBulkFixedPrice("");
  };

  // Delete selected items
  const handleDeleteSelected = () => {
    if (selectedItems.size === 0) {
      toast.warning("Selecciona al menos un item para eliminar");
      return;
    }
    
    const count = selectedItems.size;
    const newItems = items.filter((_, index) => !selectedItems.has(index));
    setItems(newItems);
    setSelectedItems(new Set());
    toast.success(`${count} item${count > 1 ? 's' : ''} eliminado${count > 1 ? 's' : ''}`);
  };

  // Handle select/deselect all items
  const handleSelectAll = () => {
    if (selectedItems.size === items.length) {
      // Deselect all
      setSelectedItems(new Set());
    } else {
      // Select all
      setSelectedItems(new Set(items.map((_, index) => index)));
    }
  };

  // Handle duplicate selected items
  const handleDuplicateSelected = () => {
    if (selectedItems.size === 0) {
      toast.warning("Selecciona al menos un item para duplicar");
      return;
    }
    
    const count = selectedItems.size;
    const selectedIndices = Array.from(selectedItems).sort((a, b) => a - b);
    
    // Create copies of selected items with new IDs
    const duplicatedItems = selectedIndices.map(index => {
      const original = items[index];
      return {
        ...original,
        id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        // Clear matched info for the copy so user can re-match if needed
        matched_type_id: null,
        matched_type_name: null,
        current_order: items.length + selectedIndices.indexOf(index)
      };
    });
    
    // Add duplicated items at the end
    setItems([...items, ...duplicatedItems]);
    setSelectedItems(new Set());
    toast.success(`${count} item${count > 1 ? 's' : ''} duplicado${count > 1 ? 's' : ''}`);
  };

  // Handle add manual item
  const handleAddItem = (newItem) => {
    setItems([...items, newItem]);
    setIsAddItemOpen(false);
  };

  // Save quotation
  // Save quotation
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`${API}/quotations/${quotation.id}`, {
        ...clientInfo,
        valid_until: validUntil || null,
        items: items,
        // P0 FIX: Send calculated subtotal_supply with UTI-SUM applied
        subtotal_supply: subtotalSupply,
        // P1 FIX: Send complete installation object with all editable fields
        installation: {
          uti_percent: installation.uti_percent,
          uti_sum: installation.uti_sum,
          cos_m2i: installation.cos_m2i,
          emo_total: installation.emo_total,
          epps_x_per: installation.epps_x_per,
          unid_desta: installation.unid_desta,
          can_pers: installation.can_pers,
          sello_total: installation.sello_total,
          adicional: installation.adicional,
          total: installationTotal
        },
        pdr: {
          quantity: pdr.quantity,
          unit_value: pdr.unit_value,
          total: pdrTotal
        },
        freight: {
          container_quantity: freight.container_quantity,
          container_cost: freight.container_cost,
          additional_costs: freight.additional_costs,
          total: freightTotal
        },
        // Section visibility flags
        show_supply: showSupply,
        show_freight: showFreight,
        show_installation: showInstallation,
        show_pdr: showPdr,
        show_otros: showOtros,
        // Otros items
        otros_items: otrosItems,
        otros_currency: otrosCurrency,
        exchange_rate: exchangeRate,
        // Service type and commercial considerations
        service_type: serviceType,
        commercial_considerations: commercialConsiderations
      });
      toast.success("Cotización guardada");
      // Clear modified items visual indicator after successful save
      setModifiedItems(new Set());
      onUpdated();
      // Refresh history after save
      if (historyOpen) {
        fetchHistory();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const detailText = Array.isArray(detail)
        ? detail.map(d => `${(d.loc || []).slice(1).join('.')}: ${d.msg}`).join('; ')
        : (typeof detail === 'string' ? detail : error.message);
      toast.error(`Error al guardar cotización${detailText ? `: ${detailText}` : ''}`);
    } finally {
      setSaving(false);
    }
  };

  // Refresh prices from database
  const handleRefreshPrices = async () => {
    setRefreshingPrices(true);
    try {
      const response = await api.post(`${API}/quotations/${quotation.id}/refresh-prices`);
      const { updated_count, changes, quotation: updatedQuotation } = response.data;
      
      if (updated_count > 0) {
        // Update local items state with new prices
        setItems(updatedQuotation.items || []);
        
        // Show detailed toast
        const totalDiff = changes.reduce((sum, c) => sum + c.difference, 0);
        const direction = totalDiff > 0 ? "aumentaron" : "disminuyeron";
        toast.success(
          `${updated_count} precios actualizados. Los costos ${direction} en promedio.`,
          { duration: 5000 }
        );
        
        // Refresh to get updated totals
        onUpdated();
      } else {
        toast.info("Todos los precios están actualizados");
      }
    } catch (error) {
      console.error("Error refreshing prices:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Error al actualizar precios";
      toast.error(errorMsg);
    } finally {
      setRefreshingPrices(false);
    }
  };

  // Export PDF - now shows preview first using the new API endpoint
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      // First save the current state
      await handleSave();
      
      // Then generate PDF
      const response = await api.post(
        `${API}/quotations/${quotation.id}/generate-pdf`,
        {},
        { responseType: 'blob' }
      );
      
      // Create URL for preview
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Store for preview
      setPdfPreviewBlob(blob);
      setPdfPreviewUrl(url);
      
      toast.success("PDF generado - Vista previa lista");
    } catch (error) {
      // console.error("Error generating PDF:", error);
      toast.error("Error al generar PDF");
    } finally {
      setExporting(false);
    }
  };

  // Quick preview using the API endpoint (opens modal directly)
  const handleQuickPreview = async () => {
    setPreviewLoading(true);
    try {
      // Save first to ensure latest data
      await handleSave();
      // Open the preview modal - it will fetch the data
      setIsPdfPreviewOpen(true);
    } catch (error) {
      toast.error("Error al guardar antes del preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Close API-based PDF preview
  const handleClosePdfPreview = () => {
    setIsPdfPreviewOpen(false);
  };
  
  // Download PDF from preview
  const handleDownloadPDF = () => {
    if (pdfPreviewBlob) {
      const url = window.URL.createObjectURL(pdfPreviewBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Cotizacion_${quotation.code}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    }
  };
  
  // Close PDF preview
  const closePdfPreview = () => {
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
    }
    setPdfPreviewUrl(null);
    setPdfPreviewBlob(null);
  };

  // Export Excel with password verification
  const handleExportExcel = async () => {
    if (!excelPassword) {
      toast.error("Ingrese la clave de exportación");
      return;
    }
    
    setExportingExcel(true);
    try {
      // First save the current state
      await handleSave();
      
      const response = await api.post(
        `${API}/quotations/${quotation.id}/export-excel`,
        { password: excelPassword },
        { responseType: 'blob' }
      );
      
      // Download the file
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Cotizacion_${quotation.code}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Excel descargado exitosamente");
      setExcelModalOpen(false);
      setExcelPassword("");
    } catch (error) {
      // console.error("Error exporting Excel:", error);
      if (error.response?.status === 403) {
        toast.error("Clave incorrecta");
      } else {
        toast.error("Error al exportar Excel");
      }
    } finally {
      setExportingExcel(false);
    }
  };

  // Handle status change
  
  // Open revision comparator and load quotations for comparison
  const handleOpenComparator = async () => {
    try {
      // Load all quotations for comparison selection
      const response = await api.get(`${API}/quotations`);
      setAllQuotationsForCompare(response.data.quotations || []);
      setIsComparatorOpen(true);
    } catch (error) {
      console.error("Error loading quotations for comparison:", error);
      toast.error("Error al cargar cotizaciones");
    }
  };
  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`${API}/quotations/${quotation.id}`, {
        status: newStatus
      });
      setCurrentStatus(newStatus);
      toast.success(`Estado cambiado a: ${statusConfig[newStatus].label}`);
      onUpdated();
    } catch (error) {
      // console.error("Error changing status:", error);
      toast.error("Error al cambiar estado");
    }
  };

  // Lookup price from index - show popup with matches
  const [lookupResults, setLookupResults] = useState(null);
  const [lookupItemIndex, setLookupItemIndex] = useState(null);
  
  const handleLookupPrice = async (index) => {
    try {
      const item = items[index];
      const response = await api.post(`${API}/quotations/auto-lookup-price`, {
        description: item.description || "",
        series: item.series || "",
        material: item.material || "",
        color: item.color || "",
        image_hash: item.image_hash || null
      });
      
      // Also get all similar types from quote_types
      const typesResponse = await api.get(`${API}/quote-types`);
      const allTypes = typesResponse.data || [];
      
      // Filter types that might match
      const searchTerms = (item.description || "").toLowerCase().split(" ").filter(t => t.length > 2);
      const filteredTypes = allTypes.filter(type => {
        const typeName = (type.name || "").toLowerCase();
        return searchTerms.some(term => typeName.includes(term)) ||
               (item.series && type.series && type.series.toLowerCase().includes(item.series.toLowerCase())) ||
               (item.material && type.material && type.material.toLowerCase().includes(item.material.toLowerCase()));
      }).slice(0, 10);
      
      // Add the auto-match result if found
      const results = [];
      if (response.data.match) {
        results.push({
          ...response.data.match,
          isAutoMatch: true,
          confidence: response.data.confidence
        });
      }
      
      // Add other filtered types that aren't duplicates
      filteredTypes.forEach(type => {
        if (!results.find(r => r.id === type.id)) {
          results.push(type);
        }
      });
      
      if (results.length > 0) {
        setLookupResults(results);
        setLookupItemIndex(index);
      } else {
        toast.info("No se encontraron tipos similares en la base de datos");
      }
    } catch (error) {
      // console.error("Error looking up price:", error);
      toast.error("Error al buscar precio");
    }
  };
  
  const applyLookupResult = (type) => {
    if (lookupItemIndex === null) return;
    
    // Support both cost_per_m2 (from auto-lookup) and average_cost_per_m2 (from quote_types)
    const cost = type.cost_per_m2 || type.average_cost_per_m2 || 0;
    
    const newItems = [...items];
    newItems[lookupItemIndex] = {
      ...newItems[lookupItemIndex],
      cost_per_m2: cost,
      matched_type_id: type.id,
      matched_type_name: type.name,
      calculated_value: (newItems[lookupItemIndex].area_m2 || 0) * cost * (newItems[lookupItemIndex].quantity || 1)
    };
    setItems(newItems);
    setLookupResults(null);
    setLookupItemIndex(null);
    toast.success(`Precio aplicado: $${cost?.toFixed(2)}/m² de "${type.name}"`);
  };

  // Auto-match all items without cost_per_m2
  const [autoMatching, setAutoMatching] = useState(false);
  
  const handleAutoMatchAll = async () => {
    setAutoMatching(true);
    let matchedCount = 0;
    let failedCount = 0;
    
    const newItems = [...items];
    
    for (let i = 0; i < newItems.length; i++) {
      const item = newItems[i];
      
      // Skip items that already have a price
      if (item.cost_per_m2 && item.cost_per_m2 > 0) continue;
      
      try {
        // Calculate image hash if we have image data but no hash
        let imageHash = item.image_hash;
        
        const response = await api.post(`${API}/quotations/auto-lookup-price`, {
          description: item.description || "",
          series: item.series || "",
          material: item.material || "",
          color: item.color || "",
          image_hash: imageHash || null
        });
        
        if (response.data.match) {
          const match = response.data.match;
          newItems[i] = {
            ...item,
            cost_per_m2: match.cost_per_m2 || match.average_cost_per_m2 || 0,
            matched_type_id: match.id,
            matched_type_name: match.name,
            calculated_value: (item.area_m2 || 0) * (match.cost_per_m2 || match.average_cost_per_m2 || 0) * (item.quantity || 1)
          };
          matchedCount++;
        } else {
          failedCount++;
        }
      } catch (error) {
        // console.error(`Error matching item ${i}:`, error);
        failedCount++;
      }
    }
    
    setItems(newItems);
    setAutoMatching(false);
    
    if (matchedCount > 0) {
      toast.success(`${matchedCount} items encontraron coincidencia con la Lista de Precios`);
    }
    if (failedCount > 0) {
      toast.info(`${failedCount} items sin coincidencia automática`);
    }
    if (matchedCount === 0 && failedCount === 0) {
      toast.info("Todos los items ya tienen precio asignado");
    }
  };

  // Función para imprimir la tabla de items
  const handlePrintItems = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Elementos - ${quotation?.code || ''}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { font-size: 18px; color: #1e40af; margin-bottom: 5px; }
          h2 { font-size: 14px; color: #64748b; margin-bottom: 15px; font-weight: normal; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background-color: #f1f5f9; padding: 8px 6px; text-align: left; border: 1px solid #e2e8f0; font-weight: 600; }
          td { padding: 6px; border: 1px solid #e2e8f0; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .total-row { background-color: #f8fafc; font-weight: 600; }
          .summary { margin-top: 20px; padding: 15px; background: #f8fafc; border-radius: 8px; }
          .summary-item { display: flex; justify-content: space-between; padding: 5px 0; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <h1>${quotation?.code} - ${quotation?.name || 'Cotización'}</h1>
        <h2>Cliente: ${clientInfo.client_company || 'N/A'} | Total: ${items.length} elementos | ${formatNumber(totalM2)} m²</h2>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Código</th>
              <th>Descripción</th>
              <th>Serie</th>
              <th>Material</th>
              <th>Color</th>
              <th class="text-center">Ancho (m)</th>
              <th class="text-center">Alto (m)</th>
              <th class="text-center">Área m²</th>
              <th class="text-center">Cant.</th>
              <th class="text-right">$/m²</th>
              <th class="text-right">Total USD</th>
            </tr>
          </thead>
          <tbody>
            ${items.map((item, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td>${item.item_code || '-'}</td>
                <td>${item.description || '-'}</td>
                <td>${item.series || '-'}</td>
                <td>${item.material || '-'}</td>
                <td>${item.color || '-'}</td>
                <td class="text-center">${formatNumber(item.width_m || 0, 3)}</td>
                <td class="text-center">${formatNumber(item.height_m || 0, 3)}</td>
                <td class="text-center">${formatNumber(item.area_m2 || 0, 4)}</td>
                <td class="text-center">${item.quantity || 1}</td>
                <td class="text-right">${formatCurrency(item.cost_per_m2 || 0)}</td>
                <td class="text-right">${formatCurrency(getItemTotalWithUtiSum(item))}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="8"></td>
              <td class="text-center"><strong>${formatNumber(totalM2)}</strong></td>
              <td></td>
              <td></td>
              <td class="text-right"><strong>${formatCurrency(subtotalSupply)}</strong></td>
            </tr>
          </tbody>
        </table>
        <div class="summary">
          <div class="summary-item"><span>Subtotal Suministro:</span><span>${formatCurrency(subtotalSupply)}</span></div>
          <div class="summary-item"><span>Instalación:</span><span>${formatCurrency(installationTotal)}</span></div>
          <div class="summary-item"><span>PDR:</span><span>${formatCurrency(pdrTotal)}</span></div>
          <div class="summary-item"><span>Flete Internacional:</span><span>${formatCurrency(freightTotal)}</span></div>
          <div class="summary-item" style="border-top: 2px solid #cbd5e1; padding-top: 10px; margin-top: 10px;">
            <span><strong>TOTAL GENERAL:</strong></span>
            <span><strong>${formatCurrency(grandTotal)}</strong></span>
          </div>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      // Use Blob URL instead of document.write for security
      const blob = new Blob([printContent], { type: 'text/html;charset=utf-8' });
      const blobUrl = URL.createObjectURL(blob);
      printWindow.location.href = blobUrl;
      printWindow.onload = () => {
        printWindow.print();
        URL.revokeObjectURL(blobUrl);
      };
    }
  };

  if (!quotation) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="font-mono text-blue-600">{quotation.code}</span>
              <span className="mx-2">-</span>
              <span>{quotation.name}</span>
              {/* Status Selector */}
              <Select value={currentStatus} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-36 h-8 ml-4">
                  <SelectValue>
                    {(() => {
                      const config = statusConfig[currentStatus];
                      const Icon = config.icon;
                      return (
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${config.color}`} />
                          <span className="text-sm">{config.label}</span>
                        </div>
                      );
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-500" />
                      Borrador
                    </div>
                  </SelectItem>
                  <SelectItem value="sent">
                    <div className="flex items-center gap-2">
                      <Send className="w-4 h-4 text-blue-500" />
                      Enviada
                    </div>
                  </SelectItem>
                  <SelectItem value="approved">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Aprobada
                    </div>
                  </SelectItem>
                  <SelectItem value="rejected">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      Rechazada
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExcelModalOpen(true)}
                data-testid="export-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleQuickPreview}
                disabled={previewLoading || items.length === 0}
                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200"
                title="Ver vista previa del PDF"
                data-testid="preview-pdf-btn"
              >
                <Eye className={`w-4 h-4 mr-2 ${previewLoading ? 'animate-pulse' : ''}`} />
                {previewLoading ? "Cargando..." : "Vista Previa"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                disabled={exporting || items.length === 0}
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? "Generando..." : "Descargar PDF"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefreshPrices}
                disabled={refreshingPrices || items.length === 0}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                title="Actualizar precios desde la lista de precios"
                data-testid="refresh-prices-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshingPrices ? 'animate-spin' : ''}`} />
                {refreshingPrices ? "Actualizando..." : "Actualizar Precios"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenComparator}
                className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 border-indigo-200"
                title="Comparar con otras versiones"
                data-testid="compare-revisions-btn"
              >
                <GitCompare className="w-4 h-4 mr-2" />
                Comparar
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
                data-testid="save-quotation-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4 pr-2">
          {/* Client Info Collapsible */}
          <ClientInfoSection
            isOpen={clientInfoOpen}
            onOpenChange={setClientInfoOpen}
            clientInfo={clientInfo}
            setClientInfo={setClientInfo}
            validUntil={validUntil}
            setValidUntil={setValidUntil}
          />

          {/* Service Type Section */}
          <ServiceTypeSection
            isOpen={serviceTypeOpen}
            onOpenChange={setServiceTypeOpen}
            serviceType={serviceType}
            setServiceType={setServiceType}
          />

          {/* Actions Bar */}
          <ActionsBar
            uploading={uploading}
            onFileUpload={handleFileUpload}
            autoMatching={autoMatching}
            onAutoMatchAll={handleAutoMatchAll}
            itemsCount={items.length}
            onAddTypeClick={() => setIsAddTypeOpen(true)}
            onAddItemClick={() => setIsAddItemOpen(true)}
          />

          {/* Items Table */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Elementos ({items.length})
                  {selectedItems.size > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {selectedItems.size} seleccionados
                    </Badge>
                  )}
                </span>
                <div className="flex items-center gap-3">
                  {items.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrintItems}
                      className="h-7 text-xs gap-1.5 text-slate-600 hover:text-blue-600 hover:border-blue-300"
                      data-testid="print-items-btn"
                    >
                      <Printer className="w-3.5 h-3.5" />
                      Imprimir Tabla
                    </Button>
                  )}
                  <span className="text-blue-600 font-mono">
                    {formatNumber(totalM2)} m² total
                  </span>
                </div>
              </CardTitle>
              
              {/* Bulk Edit Controls */}
              {items.length > 0 && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Edit2 className="w-4 h-4 text-slate-500" />
                      <span className="text-xs font-medium text-slate-600">Edición Masiva</span>
                      {selectedItems.size > 0 ? (
                        <Badge className="bg-blue-500 text-white">
                          {selectedItems.size} de {items.length} seleccionados
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">(Selecciona items con el checkbox)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleSelectAll}
                        className="h-7 text-xs"
                      >
                        {selectedItems.size === items.length ? (
                          <>
                            <Square className="w-3 h-3 mr-1" />
                            Deseleccionar
                          </>
                        ) : (
                          <>
                            <CheckSquare className="w-3 h-3 mr-1" />
                            Seleccionar Todo
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
                    <div>
                      <Label className="text-xs text-slate-500">Descripción</Label>
                      <AutocompleteInput
                        value={bulkDescription}
                        onChange={setBulkDescription}
                        suggestions={autocompleteData.descriptions}
                        placeholder="Aplicar descripción..."
                        className="h-8 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Serie</Label>
                      <AutocompleteInput
                        value={bulkSeries}
                        onChange={setBulkSeries}
                        suggestions={autocompleteData.series}
                        placeholder="Aplicar serie..."
                        className="h-8 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Material</Label>
                      <AutocompleteInput
                        value={bulkMaterial}
                        onChange={setBulkMaterial}
                        suggestions={autocompleteData.materials}
                        placeholder="Aplicar material..."
                        className="h-8 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Color</Label>
                      <AutocompleteInput
                        value={bulkColor}
                        onChange={setBulkColor}
                        suggestions={autocompleteData.colors}
                        placeholder="Aplicar color..."
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        onClick={applyAllBulkChanges}
                        disabled={selectedItems.size === 0}
                        className="h-8 text-xs flex-1"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Aplicar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleDuplicateSelected}
                        disabled={selectedItems.size === 0}
                        className="h-8 text-xs border-blue-300 text-blue-600 hover:bg-blue-50"
                        title={`Duplicar ${selectedItems.size} items seleccionados`}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleDeleteSelected}
                        disabled={selectedItems.size === 0}
                        className="h-8 text-xs border-red-300 text-red-600 hover:bg-red-50"
                        title={`Eliminar ${selectedItems.size} items seleccionados`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setBulkDescription("");
                          setBulkSeries("");
                          setBulkMaterial("");
                          setBulkColor("");
                          setBulkPricePercent("");
                          setSelectedItems(new Set());
                        }}
                        className="h-8 text-xs"
                        title="Limpiar selección y campos"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Price Percentage Adjustment Row */}
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-green-600" />
                      <span className="text-xs font-medium text-slate-600">Ajuste de Precio $/m²</span>
                      <span className="text-xs text-slate-400">(Descuento negativo, Aumento positivo)</span>
                    </div>
                    <div className="flex items-end gap-2 mt-2 flex-wrap">
                      {/* Fixed Price Field */}
                      <div className="w-32">
                        <Label className="text-xs text-slate-500">Precio Fijo $/m²</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={bulkFixedPrice}
                            onChange={(e) => setBulkFixedPrice(e.target.value)}
                            placeholder="150.00"
                            className="h-8 text-xs pl-6"
                            data-testid="bulk-fixed-price-input"
                          />
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={applyFixedPrice}
                        disabled={selectedItems.size === 0 || !bulkFixedPrice}
                        className="h-8 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                        data-testid="apply-fixed-price-btn"
                      >
                        Aplicar Precio Fijo
                      </Button>
                      
                      <div className="border-l border-slate-300 h-8 mx-2"></div>
                      
                      {/* Percentage Field */}
                      <div className="w-32">
                        <Label className="text-xs text-slate-500">% Ajuste</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            step="0.1"
                            value={bulkPricePercent}
                            onChange={(e) => setBulkPricePercent(e.target.value)}
                            placeholder="-5 o +10"
                            className="h-8 text-xs pr-6"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={applyPricePercentage}
                        disabled={selectedItems.size === 0 || !bulkPricePercent}
                        className="h-8 text-xs border-green-300 text-green-700 hover:bg-green-50"
                      >
                        <Calculator className="w-3 h-3 mr-1" />
                        Aplicar %
                      </Button>
                      {selectedItems.size > 0 && bulkPricePercent && (
                        <span className="text-xs text-slate-500">
                          {parseFloat(bulkPricePercent) >= 0 ? "+" : ""}{bulkPricePercent}% a {selectedItems.size} items
                        </span>
                      )}
                      
                      {/* % UTI SUM field */}
                      <div className="w-28 ml-4">
                        <Label className="text-xs text-slate-500">% UTI SUM</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            step="1"
                            value={installation.uti_sum ?? ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow empty string for typing, convert to number on blur or when has value
                              const numValue = value === '' ? '' : parseFloat(value);
                              setInstallation({...installation, uti_sum: numValue});
                            }}
                            onBlur={(e) => {
                              // On blur, if empty set to 0
                              if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                                setInstallation({...installation, uti_sum: 0});
                              }
                            }}
                            placeholder="38"
                            className="h-8 text-xs pr-6"
                            data-testid="uti-sum-input"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p>No hay elementos</p>
                  <p className="text-sm">Importa un Excel o agrega items manualmente</p>
                </div>
              ) : (
                /* QuotationItemsTable - Extracted component for sortable items */
                <QuotationItemsTable
                  items={items}
                  selectedItems={selectedItems}
                  modifiedItems={modifiedItems}
                  expandedItems={expandedItems}
                  toggleAllSelection={toggleAllSelection}
                  toggleItemSelection={toggleItemSelection}
                  toggleItemExpansion={toggleItemExpansion}
                  handleDragEnd={handleDragEnd}
                  setPreviewImage={setPreviewImage}
                  setPreviewItemCode={setPreviewItemCode}
                  setPreviewItemId={setPreviewItemId}
                  handleItemChange={handleItemChange}
                  handleItemChangeWithLookup={handleItemChangeWithLookup}
                  handleDeleteItem={handleDeleteItem}
                  handleLookupPrice={handleLookupPrice}
                  getItemTotalWithUtiSum={getItemTotalWithUtiSum}
                  autocompleteData={autocompleteData}
                />
              )}
            </CardContent>
          </Card>

          {/* Cost Sections Grid */}
          <CostSectionsGrid
            installation={installation}
            setInstallation={setInstallation}
            installationTotal={installationTotal}
            pdr={pdr}
            setPdr={setPdr}
            pdrTotal={pdrTotal}
            freight={freight}
            setFreight={setFreight}
            freightTotal={freightTotal}
            totalM2={totalM2}
          />

          {/* Otros Section - Miscellaneous Items */}
          <OtrosSection
            otrosItems={otrosItems}
            setOtrosItems={setOtrosItems}
            otrosCurrency={otrosCurrency}
            setOtrosCurrency={setOtrosCurrency}
            exchangeRate={exchangeRate}
            setExchangeRate={setExchangeRate}
            otrosSubtotal={otrosSubtotal}
            otrosIgv={otrosIgv}
            otrosTotal={otrosTotal}
          />

          {/* Section Visibility Toggles for PDF Export */}
          <PDFSectionsToggle
            showSupply={showSupply}
            setShowSupply={setShowSupply}
            showFreight={showFreight}
            setShowFreight={setShowFreight}
            showInstallation={showInstallation}
            setShowInstallation={setShowInstallation}
            showPdr={showPdr}
            setShowPdr={setShowPdr}
            showOtros={showOtros}
            setShowOtros={setShowOtros}
          />

          {/* Summary */}
          <CostSummaryCard
            subtotalSupply={subtotalSupply}
            installationTotal={installationTotal}
            pdrTotal={pdrTotal}
            freightTotal={freightTotal}
            otrosTotal={otrosTotal}
            otrosCurrency={otrosCurrency}
            grandTotal={grandTotal}
          />

          {/* Commercial Considerations */}
          <CommercialConsiderationsSection
            isOpen={considerationsOpen}
            onOpenChange={setConsiderationsOpen}
            considerations={commercialConsiderations}
            setConsiderations={setCommercialConsiderations}
            newConsideration={newConsideration}
            setNewConsideration={setNewConsideration}
          />

          {/* History Panel */}
          <HistorySection
            isOpen={historyOpen}
            onOpenChange={setHistoryOpen}
            history={history}
            loadingHistory={loadingHistory}
          />
        </div>

        {/* Add Item Modal */}
        <AddItemModal
          isOpen={isAddItemOpen}
          onClose={() => setIsAddItemOpen(false)}
          onAdd={handleAddItem}
        />

        {/* Add Manual Type Modal (Lista de Precio Venta) */}
        <Dialog open={isAddTypeOpen} onOpenChange={setIsAddTypeOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-green-600" />
                Añadir Tipo a Lista de Precios
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-xs text-slate-500">Nombre / Descripción *</Label>
                <Input
                  value={newTypeData.name}
                  onChange={(e) => setNewTypeData({...newTypeData, name: e.target.value})}
                  placeholder="Ej: VENTANA 2 HOJAS"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Material *</Label>
                  <Select
                    value={newTypeData.material}
                    onValueChange={(val) => setNewTypeData({...newTypeData, material: val})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="uPVC">uPVC</SelectItem>
                      <SelectItem value="Aluminio">Aluminio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Serie</Label>
                  <Input
                    value={newTypeData.series}
                    onChange={(e) => setNewTypeData({...newTypeData, series: e.target.value})}
                    placeholder="Ej: SERIE 60"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Color</Label>
                <Input
                  value={newTypeData.color}
                  onChange={(e) => setNewTypeData({...newTypeData, color: e.target.value})}
                  placeholder="Ej: BLANCO"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Ancho (m) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newTypeData.width_m}
                    onChange={(e) => setNewTypeData({...newTypeData, width_m: e.target.value})}
                    placeholder="1.20"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Alto (m) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newTypeData.height_m}
                    onChange={(e) => setNewTypeData({...newTypeData, height_m: e.target.value})}
                    placeholder="2.10"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Costo ($) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newTypeData.cost}
                    onChange={(e) => setNewTypeData({...newTypeData, cost: e.target.value})}
                    placeholder="500.00"
                    className="mt-1"
                  />
                </div>
              </div>
              {newTypeData.width_m && newTypeData.height_m && newTypeData.cost && (
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Área:</span>
                    <span className="font-mono">{(parseFloat(newTypeData.width_m) * parseFloat(newTypeData.height_m)).toFixed(4)} m²</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">$/m² calculado:</span>
                    <span className="font-mono font-semibold text-green-600">
                      ${(parseFloat(newTypeData.cost) / (parseFloat(newTypeData.width_m) * parseFloat(newTypeData.height_m))).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-500">Imagen (opcional)</Label>
                <div className="mt-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setNewTypeData({...newTypeData, image_data: ev.target.result});
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="w-full text-sm"
                  />
                  {newTypeData.image_data && (
                    <div className="mt-2 relative inline-block">
                      <img 
                        src={newTypeData.image_data} 
                        alt="Preview" 
                        className="h-16 w-auto rounded border"
                      />
                      <button
                        onClick={() => setNewTypeData({...newTypeData, image_data: null})}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsAddTypeOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveNewType}
                disabled={savingType || !newTypeData.name || !newTypeData.width_m || !newTypeData.height_m || !newTypeData.cost}
                className="bg-green-600 hover:bg-green-700"
              >
                {savingType ? "Guardando..." : "Guardar Tipo"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Image Preview Modal */}
        <ImagePreviewModal
          previewImage={previewImage}
          setPreviewImage={setPreviewImage}
          previewItemCode={previewItemCode}
          previewItemId={previewItemId}
          items={items}
          setItems={setItems}
        />

        {/* Price Lookup Results Modal */}
        <PriceLookupModal
          lookupResults={lookupResults}
          setLookupResults={setLookupResults}
          lookupItemIndex={lookupItemIndex}
          setLookupItemIndex={setLookupItemIndex}
          items={items}
          applyLookupResult={applyLookupResult}
        />

        {/* PDF Preview Modal (Legacy - blob URL) */}
        <PDFPreviewModal
          pdfPreviewUrl={pdfPreviewUrl}
          quotationCode={quotation?.code}
          closePdfPreview={closePdfPreview}
          handleDownloadPDF={handleDownloadPDF}
        />

        {/* PDF Preview Modal (API - base64) */}
        <PDFPreviewModal
          isOpen={isPdfPreviewOpen}
          onClose={handleClosePdfPreview}
          quotationId={quotation?.id}
          quotationCode={quotation?.code}
        />
        
        {/* Excel Export Password Modal */}
        <ExcelExportModal
          excelModalOpen={excelModalOpen}
          setExcelModalOpen={setExcelModalOpen}
          excelPassword={excelPassword}
          setExcelPassword={setExcelPassword}
          handleExportExcel={handleExportExcel}
          exportingExcel={exportingExcel}
        />
        
        {/* Revision Comparator Modal */}
        <RevisionComparator
          isOpen={isComparatorOpen}
          onClose={() => setIsComparatorOpen(false)}
          quotationId={quotation?.id}
          allQuotations={allQuotationsForCompare}
        />
      </DialogContent>
    </Dialog>
  );
};

export default QuotationEditorModal;
